import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { ScheduleRepository } from 'repository/scheduleRepository';
import { MeetingRepository } from 'repository/meetingRepository';
import { markOnboardingCompleted } from 'common/utils/onboarding';
import {
    AppScreen, Button, ButtonFooter, Calendar, PageIntro, Section, SectionHeader,
} from 'components/ds';
import { IcCalendar, IcClock, IcClose } from 'components/common/icons/EmojiIcons';
import styles from './ScheduleInput.module.css';
import { toLocalISOString, localInputToUtc } from 'common/utils/datetime';

interface ScheduleItem {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
}

export const ScheduleInput = () => {
    const navigate = useNavigate();
    const { meetingId } = useParams();
    const { showAlert } = useAlert();
    const { t } = useTranslation();
    const weekDaysT = t('common.weekDaysShort', { returnObjects: true }) as string[];

    const [selectedDate, setSelectedDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [scheduleList, setScheduleList] = useState<ScheduleItem[]>([]);

    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showStartTimePicker, setShowStartTimePicker] = useState(false);

    // 달력 상태

    // 시간 피커 상태
    const [tempPeriod, setTempPeriod] = useState<'오전' | '오후'>('오후');
    const [tempHour, setTempHour] = useState(6);
    const [tempMinute, setTempMinute] = useState(0);

    const hours = Array.from({ length: 12 }, (_, i) => i + 1);
    const minutes = Array.from({ length: 12 }, (_, i) => i * 5); // 5분 단위


    const MAX_SCHEDULES = 3;

    // 컴포넌트 마운트 시 기존 제안 불러오기 + ★ 모임 날짜 기본값 설정
    useEffect(() => {
        const loadExistingProposals = async () => {
            if (!meetingId) return;

            try {
                const response = await ScheduleRepository.onGetMyProposals(meetingId);
                const proposals = response.data?.data;

                if (proposals && proposals.length > 0) {
                    console.log('기존 제안 로드:', proposals);

                    // ScheduleProposal을 ScheduleItem 형식으로 변환
                    const existingSchedules = proposals.map((proposal: any) => {
                        const startDate = new Date(proposal.startDatetime);
                        const endDate = new Date(proposal.endDatetime);

                        return {
                            id: proposal.proposalId,
                            // 아래 startTime 은 로컬(getHours)인데 date 만 UTC 면 KST 오전
                            // 0~9시 일정이 전날로 잡혀 날짜와 시각이 어긋납니다.
                            date: toLocalISOString(startDate).slice(0, 10), // YYYY-MM-DD (로컬)
                            startTime: `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`,
                            endTime: `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`
                        };
                    });

                    setScheduleList(existingSchedules);
                }
            } catch (error) {
                console.error('기존 제안 로드 실패:', error);
            }
        };

        // ★ 모임의 확정/제안 일정 기준으로 selectedDate 기본값 정렬.
        //   "내 일정 입력" 진입 시 사용자가 매번 날짜 검색하지 않도록.
        //   schedule.startDatetime > scheduleStartDateTime 순으로 fallback.
        const loadMeetingDefaultDate = async () => {
            if (!meetingId) return;
            try {
                const res = await MeetingRepository.onGetMeetingDetail(meetingId);
                const m = res?.data?.data || res?.data;
                const startRaw = m?.schedule?.startDatetime || m?.scheduleStartDateTime;
                if (!startRaw) return;
                const d = new Date(startRaw);
                if (isNaN(d.getTime())) return;
                // 과거 날짜면 default 설정 안 함 (이미 끝난 모임에 새 일정 입력은 의미 없음)
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (d < today) return;
                const y = d.getFullYear();
                const mo = String(d.getMonth() + 1).padStart(2, '0');
                const da = String(d.getDate()).padStart(2, '0');
                // 달력은 고른 날짜의 달로 알아서 열립니다 (ds Calendar 가 value 로 판단)
                setSelectedDate(`${y}-${mo}-${da}`);
            } catch (e) {
                console.error('모임 일정 기본값 로드 실패:', e);
            }
        };

        loadExistingProposals();
        loadMeetingDefaultDate();
    }, [meetingId]);

    // 달력 생성 (Step2 방식)
    // 날짜 포맷팅
    const formatDateDisplay = (dateString: string) => {
        if (!dateString) return t('scheduleInput.selectDate');
        const date = new Date(dateString);
        return t('common.monthDayWeek', { month: date.getMonth() + 1, day: date.getDate(), weekday: weekDaysT[date.getDay()] });
    };

    // 시간 포맷팅
    const formatTimeDisplay = (timeString: string) => {
        if (!timeString) return '--:--';
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours);
        const period = hour >= 12 ? t('scheduleInput.pm') : t('scheduleInput.am');
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${period} ${displayHour}:${minutes}`;
    };

    // 날짜 선택

    // 이전 월
    const openTimePicker = (type: 'start' | 'end') => {
        // 종료 시간은 자동 설정되므로 시작 시간만 편집 가능
        if (type === 'end') return;

        // 기존 값이 있으면 파싱
        if (startTime) {
            const [hours, minutes] = startTime.split(':');
            const hour = parseInt(hours);
            setTempPeriod(hour >= 12 ? '오후' : '오전');
            setTempHour(hour === 0 ? 12 : hour > 12 ? hour - 12 : hour);
            setTempMinute(parseInt(minutes));
        } else {
            setTempPeriod('오후');
            setTempHour(6);
            setTempMinute(0);
        }

        setShowStartTimePicker(true);

        // 다음 프레임에서 스크롤 위치 설정
        setTimeout(() => {
        }, 100);
    };

    // 시간 선택 완료 (시작 시간만)
    const handleTimeConfirm = (type: 'start' | 'end') => {
        // 종료 시간은 편집할 수 없음
        if (type === 'end') return;

        let hour24 = tempHour;
        if (tempPeriod === '오후' && tempHour !== 12) {
            hour24 = tempHour + 12;
        } else if (tempPeriod === '오전' && tempHour === 12) {
            hour24 = 0;
        }

        const timeString = `${String(hour24).padStart(2, '0')}:${String(tempMinute).padStart(2, '0')}`;
        setStartTime(timeString);

        // 종료 시간을 시작 시간 + 3시간으로 자동 설정
        let endHour24 = hour24 + 3;
        if (endHour24 >= 24) {
            endHour24 = endHour24 - 24;
        }
        const endTimeString = `${String(endHour24).padStart(2, '0')}:${String(tempMinute).padStart(2, '0')}`;
        setEndTime(endTimeString);

        setShowStartTimePicker(false);
    };

    // 스크롤 위치를 중앙으로 이동
    const handleAddSchedule = () => {
        if (!selectedDate) {
            showAlert({ message: t('scheduleInput.alertSelectDate') });
            return;
        }
        if (!startTime) {
            showAlert({ message: t('scheduleInput.alertSelectStart') });
            return;
        }
        if (!endTime) {
            showAlert({ message: t('scheduleInput.alertSelectEnd') });
            return;
        }
        if (startTime >= endTime) {
            showAlert({ message: t('scheduleInput.alertEndAfterStart') });
            return;
        }
        // 오늘 날짜에서 이미 지난 시간은 받지 않는다 (시간 버튼이 18:00~19:30 고정이라 저녁에는 지난 시간이 그대로 보인다)
        if (new Date(`${selectedDate}T${startTime}:00`).getTime() <= Date.now()) {
            showAlert({ message: t('scheduleInput.alertPastTime') });
            return;
        }
        if (scheduleList.length >= MAX_SCHEDULES) {
            showAlert({ message: t('scheduleInput.alertMax', { max: MAX_SCHEDULES }) });
            return;
        }

        const isDuplicate = scheduleList.some(
            schedule => schedule.date === selectedDate &&
                       schedule.startTime === startTime &&
                       schedule.endTime === endTime
        );
        if (isDuplicate) {
            showAlert({ message: t('scheduleInput.alertDuplicate') });
            return;
        }

        const newSchedule: ScheduleItem = {
            id: Date.now().toString(),
            date: selectedDate,
            startTime,
            endTime
        };

        setScheduleList([...scheduleList, newSchedule]);
        setStartTime('');
        setEndTime('');
    };

    // 일정 삭제
    const handleDeleteSchedule = (id: string) => {
        setScheduleList(scheduleList.filter(schedule => schedule.id !== id));
    };

    // 완료
    const handleSubmit = async () => {
        if (scheduleList.length === 0) {
            showAlert({ message: t('scheduleInput.alertMinOne') });
            return;
        }

        if (!meetingId) {
            showAlert({ message: t('scheduleInput.alertNoMeeting') });
            return;
        }

        try {
            // ScheduleItem을 API 형식으로 변환
            const proposals = scheduleList.map(schedule => {
                // date: YYYY-MM-DD, startTime: HH:mm, endTime: HH:mm
                // 서버는 오프셋 없는 값을 UTC 로 받는다 — 벽시계 그대로 보내면 다시 열 때 9시간 밀려 보였다
                // (19:30 제안이 다음 날 04:30 으로). 다른 화면처럼 UTC 로 바꿔 보낸다.
                const startDatetime = localInputToUtc(`${schedule.date}T${schedule.startTime}:00`);
                const endDatetime = localInputToUtc(`${schedule.date}T${schedule.endTime}:00`);

                return {
                    startDatetime,
                    endDatetime
                };
            });

            console.log('제출할 일정 제안:', { meetingId, proposals });

            const response = await ScheduleRepository.onPostCreateProposals({
                meetingId,
                proposals
            });

            console.log('일정 제안 등록 응답:', response);

            // API 응답이 성공이면 (에러가 발생하지 않으면) 성공으로 간주
            markOnboardingCompleted('step_schedule');
            await showAlert({
                message: response.data?.message || t('scheduleInput.registeredCount', { count: scheduleList.length })
            });
            // 완료 후 이전 화면으로 이동 — 사용자가 진입한 곳 (모임 상세, 홈, 알림 등) 으로 자연 복귀
            navigate(-1);
        } catch (error) {
            console.error('일정 제안 등록 실패:', error);
            showAlert({ message: t('scheduleInput.submitError') });
        }
    };

    // 날짜 포맷 (MM월 DD일)
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return t('common.monthDay', { month: date.getMonth() + 1, day: date.getDate() });
    };

    // 요일
    const getDayOfWeek = (dateString: string) => {
        const date = new Date(dateString);
        return weekDaysT[date.getDay()];
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    /* 자주 고르는 시간 — 값은 24시간, 표시는 지역 형식 */
    const timeSlots = ['18:00', '18:30', '19:00', '19:30'];

    /** 시작 시간을 정하면 끝나는 시간은 +3시간으로 잡아 둡니다 */
    const pickStart = (slot: string) => {
        setStartTime(slot);
        const [h, m] = slot.split(':').map(Number);
        const endHour = (h + 3) % 24;
        setEndTime(`${String(endHour).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    };

    return (
        <AppScreen title={t('scheduleInput.title')} bar="sub" hasFooter>
            <PageIntro
                title={t('scheduleInput.guideTitle')}
                desc={
                    <>
                        {t('scheduleInput.guideMax', { max: MAX_SCHEDULES })}
                        {scheduleList.length > 0 ? ` ${t('scheduleInput.guideReplace')}` : ''}
                    </>
                }
            />

            {/* 날짜 */}
            <Section spacing="tight">
                <SectionHeader title={t('scheduleInput.labelDate')} />
                <button type="button" className={styles.picker} onClick={() => setShowDatePicker(true)}>
                    <span className={styles.pickerIcon}><IcCalendar size={18} /></span>
                    <span className={styles.pickerValue}>{formatDateDisplay(selectedDate)}</span>
                </button>
            </Section>

            {/* 시간 */}
            <Section divided>
                <SectionHeader title={t('scheduleInput.labelTime')} />
                <div className={styles.chips}>
                    {timeSlots.map(slot => (
                        <button
                            key={slot}
                            type="button"
                            className={startTime === slot ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                            onClick={() => pickStart(slot)}
                        >
                            {formatTimeDisplay(slot)}
                        </button>
                    ))}
                    <button type="button" className={styles.chip} onClick={() => openTimePicker('start')}>
                        <IcClock size={14} /> {t('scheduleInput.directInput')}
                    </button>
                </div>
                {startTime && (
                    <div className={styles.picked}>
                        <span className={styles.pickedLabel}>{t('scheduleInput.selectedTime')}</span>
                        <span className={styles.pickedValue}>{formatTimeDisplay(startTime)}</span>
                    </div>
                )}

                <div className={styles.addAction}>
                    <Button
                        variant="secondary"
                        size="lg"
                        block
                        onClick={handleAddSchedule}
                        disabled={scheduleList.length >= MAX_SCHEDULES}
                    >
                        {t('scheduleInput.addSchedule', { count: scheduleList.length, max: MAX_SCHEDULES })}
                    </Button>
                </div>
            </Section>

            {/* 등록한 일정 */}
            {scheduleList.length > 0 && (
                <Section divided last>
                    <SectionHeader
                        title={t('scheduleInput.registered')}
                        count={scheduleList.length}
                    />
                    <ul className={styles.list}>
                        {scheduleList.map((schedule) => (
                            <li key={schedule.id} className={styles.item}>
                                <span className={styles.itemWhen}>
                                    {formatDate(schedule.date)} ({getDayOfWeek(schedule.date)}){' '}
                                    {formatTimeDisplay(schedule.startTime)}
                                </span>
                                <button
                                    type="button"
                                    className={styles.itemDelete}
                                    onClick={() => handleDeleteSchedule(schedule.id)}
                                >
                                    {t('common.delete')}
                                </button>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            <ButtonFooter>
                <Button variant="ghost" size="lg" onClick={() => navigate('/')}>
                    {t('scheduleInput.home')}
                </Button>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    onClick={handleSubmit}
                    disabled={scheduleList.length === 0}
                >
                    {t('scheduleInput.done')}
                </Button>
            </ButtonFooter>

            {/* 날짜 고르기 */}
            {showDatePicker && (
                <div className={styles.overlay} onClick={() => setShowDatePicker(false)}>
                    <div
                        className={styles.sheet}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('scheduleInput.selectDate')}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.sheetHead}>
                            <h2 className={styles.sheetTitle}>{t('scheduleInput.selectDate')}</h2>
                            <button
                                type="button"
                                className={styles.sheetClose}
                                onClick={() => setShowDatePicker(false)}
                                aria-label={t('common.close')}
                            >
                                <IcClose size={20} />
                            </button>
                        </div>
                        <div className={styles.sheetBody}>
                            <Calendar
                                value={selectedDate}
                                onChange={(key) => {
                                    setSelectedDate(key);
                                    setShowDatePicker(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* 시간 직접 고르기 — 오전/오후 · 시 · 분 */}
            {showStartTimePicker && (
                <div className={styles.overlay} onClick={() => setShowStartTimePicker(false)}>
                    <div
                        className={styles.sheet}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('scheduleInput.startTime')}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.sheetHead}>
                            <h2 className={styles.sheetTitle}>{t('scheduleInput.startTime')}</h2>
                            <button
                                type="button"
                                className={styles.sheetClose}
                                onClick={() => setShowStartTimePicker(false)}
                                aria-label={t('common.close')}
                            >
                                <IcClose size={20} />
                            </button>
                        </div>

                        <div className={styles.sheetBody}>
                            <div className={styles.period}>
                                {(['오전', '오후'] as const).map(p => (
                                    <button
                                        key={p}
                                        type="button"
                                        className={tempPeriod === p
                                            ? `${styles.periodBtn} ${styles.periodBtnOn}`
                                            : styles.periodBtn}
                                        onClick={() => setTempPeriod(p)}
                                    >
                                        {p === '오전' ? t('scheduleInput.am') : t('scheduleInput.pm')}
                                    </button>
                                ))}
                            </div>

                            <span className={styles.wheelLabel}>{t('scheduleInput.hourLabel')}</span>
                            <div className={styles.wheel}>
                                {hours.map(h => (
                                    <button
                                        key={h}
                                        type="button"
                                        className={tempHour === h ? `${styles.cell} ${styles.cellOn}` : styles.cell}
                                        onClick={() => setTempHour(h)}
                                    >
                                        {h}
                                    </button>
                                ))}
                            </div>

                            <span className={styles.wheelLabel}>{t('scheduleInput.minuteLabel')}</span>
                            <div className={styles.wheel}>
                                {minutes.map(m => (
                                    <button
                                        key={m}
                                        type="button"
                                        className={tempMinute === m ? `${styles.cell} ${styles.cellOn}` : styles.cell}
                                        onClick={() => setTempMinute(m)}
                                    >
                                        {m.toString().padStart(2, '0')}
                                    </button>
                                ))}
                            </div>

                            <div className={styles.sheetAction}>
                                <Button variant="primary" size="lg" block onClick={() => handleTimeConfirm('start')}>
                                    {t('common.confirm')}
                                    {' · '}
                                    {tempPeriod === '오전' ? t('scheduleInput.am') : t('scheduleInput.pm')}{' '}
                                    {tempHour}:{tempMinute.toString().padStart(2, '0')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AppScreen>
    );
};
